import React, { useMemo, useState } from 'react';
import { Button, Input } from '../../components/base-components';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';
import {
  useCreateSeat,
  useRemoveSeat,
  useResendSeatInvite,
  useSeats,
  useSetSeatPassword,
} from '../../hooks/useApi';
import { Seat } from '../../types';
import { Badge } from '../components/Badge';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { PageShell } from '../components/PageShell';
import { Panel } from '../components/Panel';
import { Skeleton } from '../components/Skeleton';
import { UsersIcon } from '../components/icons';
import { cx, ROW_SURFACE } from '../components/styles';

/**
 * Team: the people the owner has added to their account.
 *
 * Owner-only, and the backend enforces that — a team member gets 403 from every
 * endpoint on this page, and the nav never shows them the item.
 *
 * The form leads with the email invite: leave the password blank and they
 * are emailed a code to set their own, which means the owner never handles
 * someone else's credential. Typing a password is the fallback — for when the
 * person is standing next to them, and for when email fails, which is the case
 * that matters. A bounced invite leaves a seat that is billing but unreachable,
 * so the screen has to make that state visible and fixable rather than
 * silently correct-looking.
 */

/** Mirrors `password_error` in flashtools/services/password_reset.py. */
function passwordProblem(password: string): string | null {
  if (password.length < 8) return 'At least 8 characters';
  if (/^\d+$/.test(password) || /^[a-zA-Z]+$/.test(password)) {
    return 'Needs both letters and numbers';
  }
  return null;
}

function formatWhen(iso: string | null): string {
  if (!iso) return 'Never';
  const then = new Date(iso).getTime();
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 2) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const AddMemberForm: React.FC<{ atLimit: boolean; maxSeats: number }> = ({
  atLimit,
  maxSeats,
}) => {
  const { showToast } = useToast();
  const createSeat = useCreateSeat();
  const [email, setEmail] = useState('');
  const [label, setLabel] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  // What to show after a successful add. For an invite that is just
  // reassurance; for a typed password it is the ONE moment the owner can read
  // it back, since nothing can return it afterwards.
  const [justCreated, setJustCreated] = useState<
    { email: string; password: string | null; inviteSent: boolean } | null
  >(null);

  // Blank is a valid submission — it means "email them an invite". Only a
  // password that was actually typed has to satisfy the rules.
  const pwProblem = password ? passwordProblem(password) : null;
  const canSubmit =
    !atLimit && !createSeat.isPending && email.trim().length > 3 && !pwProblem;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    try {
      const result = await createSeat.mutateAsync({
        email: email.trim(),
        password,
        label: label.trim(),
      });
      setJustCreated({
        email: email.trim().toLowerCase(),
        password: result.invited_by_email ? null : password,
        inviteSent: result.invite_sent,
      });
      setEmail('');
      setLabel('');
      setPassword('');

      // The invite warning outranks the billing one: a seat nobody can reach
      // is a worse problem than an invoice line that will reconcile itself.
      if (result.invite_warning) {
        showToast(result.invite_warning, 'error');
      } else if (result.billing_warning) {
        showToast(result.billing_warning, 'error');
      } else if (result.invited_by_email) {
        showToast('Invite sent — $5 charged now, prorated for the rest of the month', 'success');
      } else {
        showToast('Added — $5 charged now, prorated for the rest of the month', 'success');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add them');
    }
  };

  if (atLimit) {
    return (
      <p className="text-sm text-white/50">
        You have reached the limit of {maxSeats} members. Remove one to add another.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-3">
        <Input
          label="Their email"
          type="email"
          autoComplete="off"
          placeholder="alice@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          label="Name (optional)"
          placeholder="Alice — weekends"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <Input
          label="Password (optional)"
          type="text"
          autoComplete="new-password"
          placeholder="Leave blank to email them"
          value={password}
          error={Boolean(pwProblem)}
          errorMessage={pwProblem ?? undefined}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div className="sm:col-span-3 flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={!canSubmit}>
            {createSeat.isPending
              ? 'Adding…'
              : password
                ? 'Add member — $5/month'
                : 'Send invite — $5/month'}
          </Button>
          <p className="text-xs text-white/40">
            {password
              ? 'You are setting their password yourself, so no email is sent. Ask them to change it once they are in.'
              : 'They get an email with a code to set their own password. Set one here instead if you would rather hand it over directly.'}
          </p>
        </div>
      </form>

      {error && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {justCreated && justCreated.password && (
        <div className="rounded-xl border border-[#3b82f6]/20 bg-[#3b82f6]/10 px-4 py-3">
          <p className="text-sm text-white">Send them these details now.</p>
          <p className="mt-2 font-mono text-sm text-white/80">
            {justCreated.email} / {justCreated.password}
          </p>
          <p className="mt-2 text-xs text-white/50">
            This is the only time the password is shown. If it is lost, set a new one from the
            list below.
          </p>
        </div>
      )}

      {justCreated && !justCreated.password && justCreated.inviteSent && (
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3">
          <p className="text-sm text-white">Invite sent to {justCreated.email}.</p>
          <p className="mt-1 text-xs text-white/50">
            The code expires in 15 minutes. If they miss it, resend from the list below —
            they can do that as many times as they need.
          </p>
        </div>
      )}
    </div>
  );
};

const SeatRow: React.FC<{ seat: Seat }> = ({ seat }) => {
  const { showToast } = useToast();
  const removeSeat = useRemoveSeat();
  const setPassword = useSetSeatPassword();
  const resendInvite = useResendSeatInvite();
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const pwProblem = newPassword ? passwordProblem(newPassword) : null;

  const remove = async () => {
    try {
      const result = await removeSeat.mutateAsync(seat.id);
      showToast(result.billing_warning || `${seat.email} no longer has access`,
        result.billing_warning ? 'error' : 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not remove them', 'error');
    } finally {
      setConfirmingRemove(false);
    }
  };

  const resend = async () => {
    try {
      const result = await resendInvite.mutateAsync(seat.id);
      showToast(result.message, 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not send the invite', 'error');
    }
  };

  const applyPassword = async () => {
    if (pwProblem || !newPassword) return;
    try {
      await setPassword.mutateAsync({ seatId: seat.id, password: newPassword });
      showToast(`Password updated. Send it to ${seat.email}.`, 'success');
      setResetting(false);
      setNewPassword('');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not update the password', 'error');
    }
  };

  return (
    <div className={cx(ROW_SURFACE, 'p-4')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium text-white">
              {seat.label || seat.email}
            </span>
            <Badge tone="neutral" size="sm">$5/mo</Badge>
            {/* Billing starts on creation, so someone who never arrived is
                still costing the owner money. Say so on the row rather than
                letting it look settled. */}
            {!seat.has_signed_in && (
              <Badge tone="amber" size="sm">Pending</Badge>
            )}
          </div>
          {seat.label && <p className="truncate text-xs text-white/40">{seat.email}</p>}
          <p className="mt-1 text-xs text-white/40">
            {/* `has_signed_in` cannot tell an unaccepted invite from a member
                whose password was set by hand and who has not logged in yet,
                so the copy has to be true of both. "Invited" would be a lie
                about the second one. */}
            {seat.has_signed_in
              ? `Last active ${formatWhen(seat.last_seen_at)}`
              : 'Has not signed in yet'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Offered to anyone who has not arrived yet. For an unaccepted
              invite it resends the code; for a member whose password was typed
              by hand it offers them the same self-serve route instead. Both
              are useful, and "Send invite" is honest about both — "Resend"
              would not be. Gone once they are in, where it is just noise. */}
          {!seat.has_signed_in && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resend}
              disabled={resendInvite.isPending}
            >
              {resendInvite.isPending ? 'Sending…' : 'Send invite'}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setResetting((v) => !v)}>
            {resetting ? 'Cancel' : 'Set password'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setConfirmingRemove(true)}
            disabled={removeSeat.isPending}
          >
            Remove
          </Button>
        </div>
      </div>

      {resetting && (
        <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-white/[0.06] pt-4">
          <div className="min-w-[220px] flex-1">
            <Input
              label="New password"
              type="text"
              autoComplete="new-password"
              value={newPassword}
              error={Boolean(pwProblem)}
              errorMessage={pwProblem ?? undefined}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            onClick={applyPassword}
            disabled={!newPassword || Boolean(pwProblem) || setPassword.isPending}
          >
            {setPassword.isPending ? 'Saving…' : 'Save'}
          </Button>
          <p className="w-full text-xs text-white/40">
            This signs them out everywhere. You will need to pass the new password on.
          </p>
        </div>
      )}

      <ConfirmDialog
        open={confirmingRemove}
        destructive
        busy={removeSeat.isPending}
        title={`Remove ${seat.label || seat.email}?`}
        message={
          <>
            Their login stops working immediately, and the unused part of this month’s $5
            is credited against your next invoice. Transfers they already sent stay in
            your log under their name.
          </>
        }
        confirmLabel="Remove"
        onConfirm={remove}
        onClose={() => setConfirmingRemove(false)}
      />
    </div>
  );
};

export const SeatsScreen: React.FC = () => {
  const { data, isLoading, isError, error } = useSeats();

  const activeSeats = useMemo(
    () => (data?.seats ?? []).filter((s) => s.status === 'active'),
    [data],
  );

  const monthlyTotal = activeSeats.length * Number(data?.price_per_seat ?? 5);

  return (
    <PageShell>
      <PageHeader
        title="Team"
        subtitle="People who can work your inventory with you. Each one is $5/month, charged as soon as you add them, and every send they make is logged under their name."
        meta={
          data
            ? `${activeSeats.length} of ${data.max_seats} members · $${monthlyTotal.toFixed(2)}/month`
            : undefined
        }
      />

      {isError ? (
        <EmptyState
          tone="error"
          title="Could not load your team"
          body={error instanceof Error ? error.message : 'Please try again.'}
        />
      ) : (
        <div className="space-y-6">
          <Panel
            tourId="team-add"
            title="Add someone"
            hint="They get their own login and set their own password. They can see your tickets, refresh them, and send — nothing else."
          >
            {isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : (
              <AddMemberForm
                atLimit={activeSeats.length >= (data?.max_seats ?? 0)}
                maxSeats={data?.max_seats ?? 0}
              />
            )}
          </Panel>

          <Panel
            title="Your team"
            hint={activeSeats.length ? `${activeSeats.length} active` : undefined}
          >
            {isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            ) : activeSeats.length === 0 ? (
              <EmptyState
                icon={<UsersIcon className="h-6 w-6" />}
                title="Nobody on your team yet"
                body="Add someone above and they will be emailed an invite. Once they set a password they can start working your inventory."
              />
            ) : (
              <div className="space-y-3">
                {activeSeats.map((seat) => (
                  <SeatRow key={seat.id} seat={seat} />
                ))}
              </div>
            )}
          </Panel>

          <Panel title="What a team member can and cannot do" tourId="team-rules">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-white/40">Can</p>
                <ul className="mt-2 space-y-1.5 text-sm text-white/60">
                  <li>See every ticket across your connected accounts</li>
                  <li>Refresh your inventory from the platforms</li>
                  <li>Send tickets, singly or in bulk</li>
                  <li>Read the full transfer log, including your sends</li>
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-white/40">
                  Cannot
                </p>
                <ul className="mt-2 space-y-1.5 text-sm text-white/60">
                  <li>See or change billing, your card, or your plan</li>
                  <li>Connect or disconnect platform accounts</li>
                  <li>Join waitlists, buy tickets, or create listings</li>
                  <li>Add or remove team members — including themselves</li>
                </ul>
              </div>
            </div>
          </Panel>
        </div>
      )}
    </PageShell>
  );
};
